import AudiobookSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Audiobook.png';
import CalendarSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Calendar.png';
import ChancesSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Chances.png';
import ContactCardSticker from '@backrs_ui/common-ui/src/assets/images/stickers/ContactCard.png';
import CraftsDIYSticker from '@backrs_ui/common-ui/src/assets/images/stickers/CraftsDIY.png';
import DancingShoeSticker from '@backrs_ui/common-ui/src/assets/images/stickers/DancingShoes.png';
import DataChartSticker from '@backrs_ui/common-ui/src/assets/images/stickers/DataChart.png';
import DesignTechSticker from '@backrs_ui/common-ui/src/assets/images/stickers/DesignTech.png';
import FlagSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Flag.png';
import KeySticker from '@backrs_ui/common-ui/src/assets/images/stickers/Key.png';
import MagnifyingGlassSticker from '@backrs_ui/common-ui/src/assets/images/stickers/MagnifyingGlass.png';
import MerchandiseSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Merchandise.png';
import MicrophoneSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Microphone.png';
import NewspaperSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Newspaper.png';
import PodcastSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Podcast.png';
import ResumeSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Resume.png';
import SleepCloudSticker from '@backrs_ui/common-ui/src/assets/images/stickers/SleepCloud.png';
import StressThoughtSticker from '@backrs_ui/common-ui/src/assets/images/stickers/StressThought.png';
import StudyingSticker from '@backrs_ui/common-ui/src/assets/images/stickers/Studying.png';
import ThumbsUpSticker from '@backrs_ui/common-ui/src/assets/images/stickers/ThumbsUp.png';

import { Box, Theme, SxProps, Typography } from '@mui/material';

type NuggetCareerPathCoverImageProps = Readonly<{
  variant:
    | {
        type: 'CAREER_PATH';
        name: string;
      }
    | {
        type: 'COMPETENCY';
        name: string;
        slug?: string;
      };
}>;

const getCompetencySticker = (slug: string) => {
  switch (slug?.toLowerCase()) {
    // Academic competencies
    case 'academics-studying':
      return StudyingSticker;
    case 'academics-classes-majors-degrees':
      return AudiobookSticker;
    case 'foundational-learning':
      return CraftsDIYSticker;

    // Career competencies
    case 'careers-interviewing':
      return PodcastSticker;
    case 'careers-networking':
      return MicrophoneSticker;
    case 'careers-shadowing':
      return ContactCardSticker;
    case 'careers-resume':
      return ResumeSticker;
    case 'careers-social-media':
      return DesignTechSticker;

    // Mindset competencies
    case 'mindsets-growth-mindset':
      return ThumbsUpSticker;
    case 'foundational-problem-solving':
      return CalendarSticker;
    case 'mindsets-agency':
      return DancingShoeSticker;

    // Wellness competencies
    case 'wellness-stress-anxiety':
      return StressThoughtSticker;
    case 'wellness-sleep-restoration':
      return SleepCloudSticker;
    case 'wellness-family-dynamics':
      return MerchandiseSticker;
    case 'wellness-accessing-healthcare':
      return FlagSticker;
    case 'foundational-seeking-support':
      return MagnifyingGlassSticker;

    // Financial competencies
    case 'finances-credit-debt':
      return DataChartSticker;
    case 'finances-savings-investing':
      return ChancesSticker;
    case 'finances-securing-extra-cash':
      return NewspaperSticker;

    // Default fallback
    default:
      return AudiobookSticker;
  }
};

export const NuggetDefaultCoverImage = ({ variant }: NuggetCareerPathCoverImageProps) => {
  return (
    <Box sx={getContainerSx(variant.type)}>
      <Box sx={contentWrapperSx}>
        <Box sx={stickerContainerSx}>
          <Box
            component="img"
            src={
              variant.type === 'COMPETENCY' && variant.slug
                ? getCompetencySticker(variant.slug)
                : KeySticker
            }
            alt="Sticker"
            sx={stickerSx}
          />
        </Box>
        <Box sx={titleContainerSx}>
          <Typography variant="display-xs-semibold" sx={titleSx}>
            {variant.name}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const getContainerSx =
  (variantType: 'CAREER_PATH' | 'COMPETENCY'): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '100%',
    height: '200px',
    minHeight: '200px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor:
      variantType === 'CAREER_PATH'
        ? backrs.colors['Colors/Background/bg-brand-primary']
        : backrs.colors['Warning/50'],
    overflow: 'hidden',
  });

const contentWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  paddingX: backrs.spacing['spacing-xl'],
});

const titleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '60%',
  flexShrink: 0,
  marginLeft: backrs.spacing['spacing-xl'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/900'],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
});

const stickerContainerSx: SxProps<Theme> = {
  width: '40%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
};

const stickerSx: SxProps<Theme> = {
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
};
